"use client";

import { formatListingPrice } from "@/lib/currency";
import { getDealConstraintLabel } from "../../../lib/dealConstraints";
import {
  getDealTypeCategoryLabel,
  getDealTypeLabel,
  normalizeDealType,
} from "@/lib/dealTypes";
import {
  buildFinancingSummary,
  formatMoneyBrief,
  formatRateBrief,
} from "@/lib/financingTerms";
import { getCountryOption, PROPERTY_TYPE_OPTIONS } from "@/lib/listing-form";
import type {
  ListingFormFieldError,
  ListingFormMode,
  ListingFormStep,
  ListingFormValues,
} from "@/lib/listing-form";
import { listingFormStyles as s } from "./listingFormStyles";
import { theme } from "@/lib/uiTheme";

type Props = {
  mode: ListingFormMode;
  values: ListingFormValues;
  errors: ListingFormFieldError[];
  disabled?: boolean;
  onGoToStep: (step: ListingFormStep, field?: string) => void;
  onFieldChange: <K extends keyof ListingFormValues>(
    field: K,
    value: ListingFormValues[K],
  ) => void;
};

function propertyTypeLabel(value: string): string {
  return PROPERTY_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

function pinLabel(status: ListingFormValues["pinStatus"]): string {
  if (status === "manually_adjusted") return "Pin adjusted";
  if (status === "geocoded") return "Pin confirmed";
  if (status === "needs_reconfirm") return "Pin needs reconfirmation";
  return "Pin not confirmed";
}

export default function StepReview({
  mode,
  values,
  errors,
  disabled,
  onGoToStep,
  onFieldChange,
}: Props) {
  const country = getCountryOption(values.country);
  const priceNum = Number(String(values.price).replace(/,/g, ""));
  const cover =
    values.images.find((i) => i.isCover) ?? values.images[0] ?? null;
  const dealType = normalizeDealType(values.deal_type);
  const financing = dealType
    ? buildFinancingSummary(dealType, values.financing)
    : null;

  return (
    <section style={s.panel}>
      <h2 style={s.sectionTitle}>
        Review and {mode === "create" ? "publish" : "save"}
      </h2>
      <p style={s.sectionHint}>
        Confirm the details below. Use Edit to jump back to a step.
      </p>

      {errors.length > 0 ? (
        <div style={s.errorBanner} role="alert">
          <strong style={{ display: "block", marginBottom: 8 }}>
            Fix these before continuing
          </strong>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((err) => (
              <li
                key={`${err.field}-${err.message}`}
                style={{ marginBottom: 6 }}
              >
                <button
                  type="button"
                  onClick={() => onGoToStep(err.step, err.field)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: theme.colors.danger,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: 14,
                    textAlign: "left",
                  }}
                >
                  Step {err.step}: {err.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <ReviewGroup title="Location" onEdit={() => onGoToStep(1)}>
        <ReviewRow label="Country" value={country.label} />
        <ReviewRow
          label="Address"
          value={[values.address, values.city, values.state, values.zip]
            .filter(Boolean)
            .join(", ")}
        />
        <ReviewRow label="Map" value={pinLabel(values.pinStatus)} />
      </ReviewGroup>

      <ReviewGroup title="Deal details" onEdit={() => onGoToStep(2)}>
        <ReviewRow
          label="Property type"
          value={propertyTypeLabel(values.property_type) || "—"}
        />
        <ReviewRow
          label="Asking price"
          value={
            Number.isFinite(priceNum) && priceNum > 0
              ? formatListingPrice(priceNum, values.currency)
              : "—"
          }
        />
        <ReviewRow label="Currency" value={values.currency} />
        <ReviewRow
          label="ARV"
          value={
            values.arv
              ? formatListingPrice(Number(values.arv), values.currency)
              : "—"
          }
        />
        <ReviewRow
          label="Repairs"
          value={
            values.repairs
              ? formatListingPrice(Number(values.repairs), values.currency)
              : "—"
          }
        />
        <ReviewRow
          label="Specs"
          value={
            [
              values.beds ? `${values.beds} bed` : null,
              values.baths ? `${values.baths} bath` : null,
              values.sqft ? `${values.sqft} sqft` : null,
              values.lot_sqft ? `${values.lot_sqft} lot` : null,
              values.year_built ? `built ${values.year_built}` : null,
              values.garage_spaces ? `${values.garage_spaces} garage` : null,
            ]
              .filter(Boolean)
              .join(" · ") || "—"
          }
        />
        <ReviewRow
          label="55+ / age restricted"
          value={values.age_restricted ? "Yes" : "No"}
        />
        <ReviewRow
          label="Constraints"
          value={
            values.deal_constraints.length
              ? values.deal_constraints.map(getDealConstraintLabel).join(", ")
              : "None"
          }
        />
        {values.deal_constraints_notes.trim() ? (
          <ReviewRow
            label="Constraint notes"
            value={values.deal_constraints_notes.trim()}
          />
        ) : null}
      </ReviewGroup>

      <ReviewGroup title="Deal structure" onEdit={() => onGoToStep(2)}>
        <ReviewRow
          label="Deal type"
          value={getDealTypeLabel(values.deal_type) ?? "Not selected"}
        />
        {getDealTypeCategoryLabel(values.deal_type) ? (
          <ReviewRow
            label="Category"
            value={getDealTypeCategoryLabel(values.deal_type) as string}
          />
        ) : null}
        {financing?.cash_needed != null ? (
          <ReviewRow
            label="Cash needed"
            value={
              formatMoneyBrief(financing.cash_needed, values.currency) as string
            }
          />
        ) : null}
        {financing?.monthly_payment != null ? (
          <ReviewRow
            label="Payment"
            value={`${formatMoneyBrief(financing.monthly_payment, values.currency)} per month`}
          />
        ) : null}
        {financing?.interest_rate != null ? (
          <ReviewRow
            label="Rate"
            value={formatRateBrief(financing.interest_rate) as string}
          />
        ) : null}
        {financing?.existing_loan_balance != null ? (
          <ReviewRow
            label="Existing balance"
            value={
              formatMoneyBrief(
                financing.existing_loan_balance,
                values.currency,
              ) as string
            }
          />
        ) : null}
        {financing?.balloon_due_date ? (
          <ReviewRow label="Balloon" value={financing.balloon_due_date} />
        ) : null}
        {dealType === "subject_to" ? (
          <ReviewRow
            label="Due-on-sale acknowledgment"
            value={
              financing?.due_on_sale_acknowledged
                ? "Acknowledged"
                : "Not acknowledged"
            }
          />
        ) : null}
      </ReviewGroup>

      <ReviewGroup title="Photos and contact" onEdit={() => onGoToStep(3)}>
        <ReviewRow
          label="Photos"
          value={`${values.images.length} photo${values.images.length === 1 ? "" : "s"}${
            cover ? " · cover selected" : ""
          }`}
        />
        <ReviewRow
          label="Description"
          value={values.description.trim() || "—"}
        />
        <ReviewRow
          label="Contact name"
          value={values.contact_name.trim() || "—"}
        />
        <ReviewRow
          label="Contact phone"
          value={values.contact_phone.trim() || "—"}
        />
        <ReviewRow
          label="Contact email"
          value={values.contact_email.trim() || "—"}
        />
      </ReviewGroup>

      <div style={{ ...s.panel, padding: 16, marginTop: 8, boxShadow: "none" }}>
        {mode === "create" ? (
          <label style={s.checkboxRow}>
            <input
              id="listing-seller-ack"
              name="seller_acknowledged"
              type="checkbox"
              checked={values.seller_acknowledged}
              disabled={disabled}
              onChange={(e) =>
                onFieldChange("seller_acknowledged", e.target.checked)
              }
              style={{
                width: 18,
                height: 18,
                marginTop: 2,
                accentColor: theme.colors.primary,
              }}
            />
            <span
              style={{ fontSize: 14, color: theme.colors.ink, lineHeight: 1.5 }}
            >
              I confirm that I own this listing, represent the owner, or am
              otherwise authorized to market this deal. I understand that
              unauthorized listings may be removed.
            </span>
          </label>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: theme.colors.inkMuted,
              lineHeight: 1.5,
            }}
          >
            By saving, you confirm you remain authorized to market this deal.
            Unauthorized listings may be removed.
          </p>
        )}
      </div>
    </section>
  );
}

function ReviewGroup({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={s.reviewGroup}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 700,
            color: theme.colors.ink,
          }}
        >
          {title}
        </h3>
        <button
          type="button"
          onClick={onEdit}
          style={{ ...s.btnGhost, padding: "6px 12px", fontSize: 13 }}
        >
          Edit
        </button>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={s.reviewRow}>
      <span style={s.reviewLabel}>{label}</span>
      <span style={s.reviewValue}>{value}</span>
    </div>
  );
}
